import React, { useState, useEffect } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Quote } from "lucide-react";
import { QuoteEditorProps } from "./QuoteEditor.types";
import { QuoteBlockContent } from "@/modules/hub/features/ResourceManagement/components/SortableBlock/types";

export function QuoteEditor({ value, onChange }: QuoteEditorProps) {
  const [text, setText] = useState("");
  const [attribution, setAttribution] = useState("");

  useEffect(() => {
    if (value) {
      try {
        const parsed = JSON.parse(value) as QuoteBlockContent;
        setText(parsed.text || "");
        setAttribution(parsed.attribution || "");
      } catch (e) {
        console.error("Failed to parse quote content:", e);
        setText("");
        setAttribution("");
      }
    }
  }, [value]);

  const updateContent = (newText: string, newAttribution: string) => {
    const content: QuoteBlockContent = {
      text: newText,
      attribution: newAttribution || undefined,
    };
    onChange(JSON.stringify(content));
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newText = e.target.value;
    setText(newText);
    updateContent(newText, attribution);
  };

  const handleAttributionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newAttribution = e.target.value;
    setAttribution(newAttribution);
    updateContent(text, newAttribution);
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="quote-text">Quote Text</Label>
        <Textarea
          className="min-h-[100px]"
          id="quote-text"
          onChange={handleTextChange}
          placeholder="Enter the quote text..."
          value={text}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="quote-attribution">Attribution (Optional)</Label>
        <Input
          id="quote-attribution"
          onChange={handleAttributionChange}
          placeholder="e.g., John Doe, CEO of Example Corp"
          type="text"
          value={attribution}
        />
      </div>

      <div className="space-y-2">
        <Label>Preview</Label>
        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-6 border-l-4 border-blue-500">
          <div className="flex gap-4">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                <Quote className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
            <blockquote className="flex-1">
              <p className="text-lg italic text-gray-700 dark:text-gray-300 leading-relaxed">
                "{text || "Your quote will appear here..."}"
              </p>
              {attribution && (
                <footer className="mt-3 text-sm font-medium text-gray-600 dark:text-gray-400">
                  — {attribution}
                </footer>
              )}
            </blockquote>
          </div>
        </div>
      </div>
    </div>
  );
}
